import { injectable } from 'tsyringe';

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const SCRIPT_ID = 'facebook-jssdk';
const GRAPH_API_VERSION = 'v26.0';

interface FacebookLoginOptions {
  config_id: string;
  response_type: string;
  override_default_response_type: boolean;
  extras: {
    setup: {
      solutionID: string;
    };
  };
}

export interface FacebookSdk {
  init(options: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }): void;
  login(callback: () => void, options: FacebookLoginOptions): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

@injectable()
export class FacebookSdkLoader {
  private pending: Promise<FacebookSdk> | null = null;
  private sdk: FacebookSdk | null = null;

  get(): FacebookSdk | null {
    return this.sdk;
  }

  load(appId: string): Promise<FacebookSdk> {
    if (!this.pending) {
      this.pending = new Promise<FacebookSdk>((resolve, reject) => {
        if (window.FB) {
          resolve(this.initialise(appId));

          return;
        }

        window.fbAsyncInit = () => resolve(this.initialise(appId));

        this.injectScript().catch(reject);
      }).catch(error => {
        this.pending = null;

        throw error;
      });
    }

    return this.pending;
  }

  private injectScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById(SCRIPT_ID)) {
        resolve();

        return;
      }

      const script = document.createElement('script');

      script.id = SCRIPT_ID;
      script.src = SDK_URL;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load the Facebook SDK'));

      document.head.appendChild(script);
    });
  }

  private initialise(appId: string): FacebookSdk {
    const sdk = window.FB;

    if (!sdk) {
      throw new Error('Facebook SDK loaded without exposing window.FB');
    }

    sdk.init({ appId, autoLogAppEvents: true, xfbml: true, version: GRAPH_API_VERSION });
    this.sdk = sdk;

    return sdk;
  }
}
